import {
  apiClient,
} from "./apiClient";

import type {
  CampaignDeliveryLogsData,
  CampaignExecutionData,
  CampaignExecutionsData,
  CampaignRecipientsData,
  CampaignSendNowData,
  CampaignSummary,
  CreateNotificationCampaignInput,
  ListCampaignDeliveryLogsQuery,
  ListCampaignExecutionsQuery,
  ListCampaignRecipientsQuery,
  ListNotificationCampaignsQuery,
  NotificationCampaignData,
  NotificationCampaignsListData,
  UpdateNotificationCampaignInput,
} from "../types/notification-campaigns.types";

function buildQuery<
  T extends object
>(
  values: T,
): string {
  const params =
    new URLSearchParams();

  Object.entries(
    values,
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        value ===
          undefined ||
        value ===
          null ||
        value ===
          ""
      ) {
        return;
      }

      params.set(
        key,
        String(
          value,
        ),
      );
    },
  );

  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}

export const notificationCampaignsApi = {
  list(
    query:
      ListNotificationCampaignsQuery =
        {},
  ) {
    return apiClient.get<
      NotificationCampaignsListData
    >(
      `/notification-campaigns${buildQuery(
        query,
      )}`,
    );
  },

  getById(
    campaignId: string,
  ) {
    return apiClient.get<
      NotificationCampaignData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}`,
    );
  },

  create(
    input:
      CreateNotificationCampaignInput,
  ) {
    return apiClient.post<
      NotificationCampaignData
    >(
      "/notification-campaigns",
      input,
    );
  },

  update(
    campaignId: string,
    input:
      UpdateNotificationCampaignInput,
  ) {
    return apiClient.patch<
      NotificationCampaignData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}`,
      input,
    );
  },

  pause(
    campaignId: string,
  ) {
    return apiClient.post<
      NotificationCampaignData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/pause`,
      {},
    );
  },

  resume(
    campaignId: string,
  ) {
    return apiClient.post<
      NotificationCampaignData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/resume`,
      {},
    );
  },

  cancel(
    campaignId: string,
  ) {
    return apiClient.post<
      NotificationCampaignData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/cancel`,
      {},
    );
  },

  sendNow(
    campaignId: string,
  ) {
    return apiClient.post<
      CampaignSendNowData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/send-now`,
      {},
    );
  },

  getSummary(
    campaignId: string,
  ) {
    return apiClient.get<
      CampaignSummary
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/summary`,
    );
  },

  getExecutions(
    campaignId: string,
    query:
      ListCampaignExecutionsQuery =
        {},
  ) {
    return apiClient.get<
      CampaignExecutionsData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/executions${buildQuery(
        query,
      )}`,
    );
  },

  getExecution(
    campaignId: string,
    executionId: string,
  ) {
    return apiClient.get<
      CampaignExecutionData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/executions/${encodeURIComponent(
        executionId,
      )}`,
    );
  },

  getRecipients(
    campaignId: string,
    executionId: string,
    query:
      ListCampaignRecipientsQuery =
        {},
  ) {
    return apiClient.get<
      CampaignRecipientsData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/executions/${encodeURIComponent(
        executionId,
      )}/recipients${buildQuery(
        query,
      )}`,
    );
  },

  getDeliveryLogs(
    campaignId: string,
    executionId: string,
    query:
      ListCampaignDeliveryLogsQuery =
        {},
  ) {
    return apiClient.get<
      CampaignDeliveryLogsData
    >(
      `/notification-campaigns/${encodeURIComponent(
        campaignId,
      )}/executions/${encodeURIComponent(
        executionId,
      )}/delivery-logs${buildQuery(
        query,
      )}`,
    );
  },
};